// Viewport-aware placement for the collapsed rail's flyout submenus (DQ-8763).
//
// The flyout is `position: absolute; top: 0` inside its `.nav-item`, so a parent
// sitting low on the rail — `Administrative` is always the last one — opens a
// panel that runs past the bottom of the viewport on a 13" screen. The reveal
// itself stays in CSS (`:hover` / `:focus-within`); this module only re-anchors
// the open panel and caps its height so the rows stay reachable.

// The flyout is never pulled above the logo header. `.sidebar-logo` is a 61px
// box, but the app sets no global border-box reset, so its rendered height is
// 61 + 18px padding x2 + a 1px bottom border. The live height is measured where
// the element is available; this is the fallback and the SCSS cap's basis.
export const FLYOUT_HEADER_OFFSET = 98;
export const FLYOUT_VIEWPORT_MARGIN = 8;

export interface FlyoutPlacementInput {
  // Viewport-relative top of the parent row the flyout is anchored to.
  triggerTop: number;
  // Viewport-relative top of the `.nav-item` the flyout is positioned against.
  itemTop: number;
  // Natural height of the submenu content.
  contentHeight: number;
  viewportHeight: number;
  // Rendered height of the sidebar's logo header.
  headerOffset?: number;
}

export interface FlyoutPlacement {
  // `top` offset relative to the `.nav-item` offset parent.
  top: number;
  maxHeight: number;
}

export const computeFlyoutPlacement = ({
  triggerTop,
  itemTop,
  contentHeight,
  viewportHeight,
  headerOffset = FLYOUT_HEADER_OFFSET,
}: FlyoutPlacementInput): FlyoutPlacement => {
  const minTop = headerOffset + FLYOUT_VIEWPORT_MARGIN;
  const maxBottom = Math.max(viewportHeight - FLYOUT_VIEWPORT_MARGIN, minTop);
  const available = maxBottom - minTop;
  const maxHeight = Math.max(Math.min(contentHeight, available), 0);
  // Keep the panel aligned with its row where it fits, then slide it up just
  // far enough to stay inside the viewport.
  const anchoredTop = Math.min(
    Math.max(triggerTop, minTop),
    maxBottom - maxHeight,
  );

  return {
    top: Math.round(anchoredTop - itemTop),
    maxHeight: Math.round(maxHeight),
  };
};

// The open flyout is the one whose row is hovered or holds focus — the same
// condition the CSS reveal keys off.
export const findOpenFlyoutItem = (
  panel: HTMLElement | null,
): HTMLElement | null =>
  panel?.querySelector<HTMLElement>(
    ".nav-item:hover, .nav-item:focus-within",
  ) ?? null;

const getParts = (item: HTMLElement) => {
  const submenu = item.querySelector<HTMLElement>(".nav-submenu");
  const trigger = item.querySelector<HTMLElement>(".expandable-link");

  return submenu && trigger ? { submenu, trigger } : null;
};

/**
 * Re-anchors the flyout inside `item` so it fits the viewport, adding an
 * internal scrollbar when the submenu is taller than the space available.
 */
export const positionFlyout = (item: HTMLElement): void => {
  const parts = getParts(item);
  if (!parts) return;

  const { submenu, trigger } = parts;

  // `scrollHeight` reports the full content height regardless of the max-height
  // cap, so the panel is never resized to measure it — lifting the cap would
  // reset `scrollTop`, throwing away the user's scroll position when focus
  // moves onto a submenu link.
  const contentHeight = submenu.scrollHeight;

  const headerHeight = item
    .closest(".left-panel")
    ?.querySelector<HTMLElement>(".sidebar-logo")
    ?.getBoundingClientRect().height;

  const placement = computeFlyoutPlacement({
    triggerTop: trigger.getBoundingClientRect().top,
    itemTop: item.getBoundingClientRect().top,
    contentHeight,
    viewportHeight: window.innerHeight,
    // Falls back to the known height when layout is unmeasurable (jsdom).
    headerOffset: headerHeight || undefined,
  });

  submenu.style.top = `${placement.top}px`;
  submenu.style.maxHeight = `${placement.maxHeight}px`;
  // `auto` rather than `scroll`: the internal scrollbar only appears on the
  // short viewports where the capped panel cannot show every row.
  submenu.style.overflowY = "auto";
  submenu.style.overflowX = "hidden";
};

/**
 * Drops the inline placement again so the CSS close transition (`max-height: 0`)
 * takes over when the pointer or focus leaves the item.
 */
export const clearFlyoutPosition = (item: HTMLElement): void => {
  const parts = getParts(item);
  if (!parts) return;

  const { submenu } = parts;

  submenu.style.top = "";
  submenu.style.maxHeight = "";
  submenu.style.overflowY = "";
  submenu.style.overflowX = "";
};
